import { Controller } from "@hotwired/stimulus";

// The event page's reply composer (Events v2), restaurant/party_inquiries/_reply. The message is a Trix editor; a
// quick reply fills it, asking before it replaces what staff typed, and the booked-that-day one turns on "mark lost".
// Files picked on the device add up across picks and each can come off; library files are checkboxes. The meter counts
// both against the 7 MB one email holds and holds Send while the set is over it (the server refuses it too). The
// draft (the message, the library picks and the Lost tick) waits in localStorage until the reply goes out (`state` "sent"), so a
// failed send or a closed tab keeps it; on a phone a failed send reopens the sheet. A browser never hands a page its
// files back, so device files are picked again.
export default class extends Controller {
  static targets = ["editor", "chip", "lost", "lostBox", "sheet", "send", "fileInput", "fileList", "libraryFile", "meter"];
  static values = { templates: Array, draftKey: String, state: String, types: Array, limit: Number, maxFiles: Number, lostConfirm: String };

  connect() {
    this.filled = null;
    this.files = [];
    this.refused = null;
    this.over = false;
    this.busy = false;
    this.restored = false;
    if (this.stateValue === "sent") this.store(null);
    if (this.editor) this.restore();
    if (this.stateValue === "failed" && window.matchMedia("(max-width: 767.98px)").matches) {
      bootstrap.Offcanvas.getOrCreateInstance(this.sheetTarget).show();
    }
  }

  get editor() {
    return this.hasEditorTarget ? this.editorTarget.editor : null;
  }

  // Once Trix is ready: on trix-initialize, or at connect when it already was. The library picks and the Lost tick go
  // first, so the trix-change the loaded draft fires saves them along with it.
  restore() {
    if (this.restored || !this.editor) return;
    this.restored = true;

    const draft = this.read();
    if (draft && !this.text()) {
      this.libraryFileTargets.forEach((box) => {
        box.checked = draft.library.includes(box.value);
      });
      if (draft.lost && this.hasLostTarget) {
        this.lostTarget.hidden = false;
        this.lostBoxTarget.checked = true;
      }
      if (draft.html) this.editor.loadHTML(draft.html);
    }
    this.count();
  }

  pick({ params: { key } }) {
    const reply = this.templatesValue.find((template) => template.key === key);
    if (!reply || !this.editor) return;

    const typed = this.text();
    if (typed && typed !== this.filled && !window.confirm("Replace your message with this quick reply?")) return;

    this.editor.loadHTML(this.html(reply.body));
    this.filled = this.text();
    this.chipTargets.forEach((chip) => {
      chip.setAttribute("aria-pressed", String(chip.dataset.partyReplyKeyParam === key));
    });
    if (this.hasLostTarget) {
      this.lostTarget.hidden = !reply.marks_lost;
      this.lostBoxTarget.checked = reply.marks_lost;
      this.save();
    }
    this.editorTarget.focus();
  }

  // A quick reply is plain text. Trix keeps typed line breaks inside one block, so the reply goes in the same way.
  html(text) {
    const escaper = document.createElement("div");
    escaper.textContent = text.trim();
    return `<div>${escaper.innerHTML.replace(/\n/g, "<br>")}</div>`;
  }

  text() {
    return this.editor ? this.editor.getDocument().toString().trim() : "";
  }

  // A pick adds to the files already there. A type the server would refuse is left out, with the reason. A file the
  // browser can't type (an empty type) goes to the server, which reads its signature.
  addFiles() {
    const picked = Array.from(this.fileInputTarget.files);
    const allowed = (file) => !file.type || this.typesValue.includes(file.type);
    const refused = picked.find((file) => !allowed(file));
    picked.forEach((file) => {
      const kept = this.files.some((other) => other.name === file.name && other.size === file.size &&
        other.lastModified === file.lastModified);
      if (!kept && allowed(file)) this.files.push(file);
    });
    this.refused = refused ? `${refused.name} can't go with a reply. Attach PDFs and images (PNG, JPEG or WebP).` : null;
    this.syncFiles();
  }

  removeFile({ params: { index } }) {
    this.files.splice(index, 1);
    this.refused = null;
    this.syncFiles();
    this.fileInputTarget.focus();
  }

  changeLibrary() {
    this.count();
    this.save();
  }

  // The input posts what the list shows. Without DataTransfer the input keeps only its last pick, as browsers do,
  // and the list follows it.
  syncFiles() {
    try {
      const transfer = new DataTransfer();
      this.files.forEach((file) => transfer.items.add(file));
      this.fileInputTarget.files = transfer.files;
    } catch (_error) {
      this.files = Array.from(this.fileInputTarget.files);
    }

    const list = this.fileListTarget;
    list.textContent = "";
    this.files.forEach((file, index) => list.append(this.fileItem(file, index)));
    list.hidden = this.files.length === 0;
    this.count();
  }

  fileItem(file, index) {
    const item = document.createElement("li");
    item.className = "ev-reply__file";
    const icon = document.createElement("i");
    icon.className = `bi ${file.type === "application/pdf" ? "bi-file-earmark-pdf" : "bi-file-earmark-image"}`;
    icon.setAttribute("aria-hidden", "true");
    const name = document.createElement("span");
    name.className = "ev-reply__file-name";
    name.textContent = file.name;
    const size = document.createElement("span");
    size.className = "ev-reply__file-size";
    size.textContent = this.size(file.size);
    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "ev-reply__file-remove";
    remove.dataset.action = "party-reply#removeFile";
    remove.dataset.partyReplyIndexParam = index;
    const cross = document.createElement("i");
    cross.className = "bi bi-x-lg";
    cross.setAttribute("aria-hidden", "true");
    const label = document.createElement("span");
    label.className = "visually-hidden";
    label.textContent = `Remove ${file.name}`;
    remove.append(cross, label);
    item.append(icon, name, size, remove);
    return item;
  }

  // The same limits and words as PartyInquiries::ReplyFiles.
  count() {
    const library = this.libraryFileTargets.filter((box) => box.checked);
    const total = this.files.length + library.length;
    const bytes = this.files.reduce((sum, file) => sum + file.size, 0) +
      library.reduce((sum, box) => sum + Number(box.dataset.bytes), 0);
    let status = null;
    if (total > this.maxFilesValue) {
      status = `Attach up to ${this.maxFilesValue} files to one reply.`;
    } else if (bytes > this.limitValue) {
      status = `These files come to ${this.size(bytes)}, and one email holds ${this.size(this.limitValue)}. ` +
        "Take one off, or send it in a second reply.";
    } else if (total > 0) {
      status = `${total} ${total === 1 ? "file" : "files"}, ${this.size(bytes)} of ${this.size(this.limitValue)}`;
    }
    this.over = total > this.maxFilesValue || bytes > this.limitValue;

    const text = [this.refused, status].filter(Boolean).join(" ");
    this.meterTarget.textContent = text;
    this.meterTarget.hidden = !text;
    this.meterTarget.classList.toggle("text-danger", this.over || Boolean(this.refused));
    this.syncSend();
  }

  // Three significant digits, as number_to_human_size writes them.
  size(bytes) {
    const units = ["Bytes", "KB", "MB", "GB"];
    let value = bytes;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
      value /= 1024;
      unit += 1;
    }
    return `${Number(value.toPrecision(3))} ${units[unit]}`;
  }

  // A second tap can't email the guest twice, and a set over the limit doesn't go (Enter in the address field
  // submits too). Marking the event lost asks first, as the stage menu's Mark lost does, when it would cancel what the
  // guest could still pay. A send that doesn't go stops here, before rails-ujs disables the button for it.
  sending(event) {
    if (this.over || (this.lostBoxChecked() && this.lostConfirmValue && !window.confirm(this.lostConfirmValue))) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.busy = true;
    this.syncSend();
  }

  lostBoxChecked() {
    return this.hasLostBoxTarget && this.lostBoxTarget.checked;
  }

  // A page brought back by the Back button keeps its disabled button.
  ready() {
    this.busy = false;
    this.syncSend();
  }

  syncSend() {
    this.sendTargets.forEach((button) => {
      button.disabled = this.busy || this.over;
    });
  }

  focus() {
    if (this.hasEditorTarget) this.editorTarget.focus();
  }

  save() {
    if (!this.hasEditorTarget) return;

    const library = this.libraryFileTargets.filter((box) => box.checked).map((box) => box.value);
    const html = this.text() ? this.editorTarget.value : "";
    const lost = this.lostBoxChecked();
    this.store(html || library.length ? { html, library, lost } : null);
  }

  // Storage can throw (private mode, blocked site data) and hold anything; the composer works without it.
  read() {
    try {
      const draft = JSON.parse(window.localStorage.getItem(this.draftKeyValue));
      if (!draft || typeof draft !== "object") return null;
      return {
        html: String(draft.html || ""),
        library: Array.isArray(draft.library) ? draft.library.map(String) : [],
        lost: draft.lost === true,
      };
    } catch (_error) {
      return null;
    }
  }

  store(draft) {
    try {
      if (draft) {
        window.localStorage.setItem(this.draftKeyValue, JSON.stringify(draft));
      } else {
        window.localStorage.removeItem(this.draftKeyValue);
      }
    } catch (_error) {
      // Nothing to keep.
    }
  }
}
